import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SipConfig, SipConfigCollection, SipConfigImportResult, SipConfigImportRow, SipConfigUpdate } from '../models/sip.model';
import { RestUtils } from './rest-utils';

@Injectable({ providedIn: 'root' })
export class SipService {
  http: HttpClient;

  utils = new RestUtils();

  headers = new HttpHeaders();

  readonly protocols = ['UDP', 'TCP'];

  constructor(http: HttpClient) {
    this.http = http;
    this.headers = this.headers.set('Content-Type', 'application/json');
  }

  allConfig(params: SipConfigRequestParams): Observable<SipConfigCollection> {
    const queryParams = {
      page: params.page,
      size: params.size,
      sort: params.sort.length ? params.sort : null,
      search: params.search ? params.search : null,
    };
    const url = this.utils.buildUrl('ROLE/sip/sip-configs', queryParams);
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.get<SipConfigCollection>(url, options).pipe(
      map(configs => {
        configs.content = configs.content.map(config => this.prepareConfig(config));
        return configs;
      })
    );
  }

  oneConfig(id: number): Observable<SipConfig> {
    const url = this.utils.buildUrl('ROLE/sip/sip-configs/' + id);
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.get<SipConfig>(url, options).pipe(map(this.prepareConfig));
  }

  saveConfig(config: SipConfigUpdate): Observable<SipConfig> {
    const options = this.utils.getHttpHeaderOptions(this.headers);
    if (config.id) {
      const url = this.utils.buildUrl('ROLE/sip/sip-configs/' + config.id);
      return this.http.put<SipConfig>(url, config, options);
    }

    const url = this.utils.buildUrl('ROLE/sip/sip-configs');
    return this.http.post<SipConfig>(url, config, options);
  }

  importConfigs(rows: SipConfigImportRow[]): Observable<SipConfigImportResult> {
    const url = this.utils.buildUrl('ROLE/sip/sip-configs/import');
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post<SipConfigImportResult>(url, rows, options);
  }

  getImportExampleFileUrl() {
    return window.location.origin + '/templates/import_sip_configurations.xlsx?v=0_1_1';
  }

  private prepareConfig(config: SipConfig): SipConfig {
    config.createdAt = new Date(config.createdAt);
    if (config.updatedAt) {
      config.updatedAt = new Date(config.updatedAt);
    }
    config.batch = false;
    return config;
  }

  deleteConfig(id: number): Observable<unknown> {
    const url = this.utils.buildUrl('ROLE/sip/sip-configs/' + id);
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.delete(url, options);
  }

  createConfig(): SipConfigUpdate {
    return {
      id: null,
      name: '',
      host: '',
      port: 5060,
      protocol: 'UDP',
      prefix: null,
      enabled: true,
      pingEnabled: true,
      cps: 1,
      username: null,
      password: null,
    };
  }
}

export class SipConfigRequestParams {
  size = 20;
  page = 0;
  search: string;

  sort: string[] = [];

  setSort(propertyName: string, direction: string) {
    this.sort.push(propertyName + (direction === 'desc' ? ',desc' : ''));
  }

  removeSort(propertyName: string) {
    this.sort = this.sort.filter(_ => _.indexOf(propertyName) === -1);
  }

  resetSort() {
    this.sort = [];
  }
}
